import React, { ChangeEvent, FC, useMemo, useState } from "react";
import { Container, RadioButtonWithLabel } from "../../../../components";
import {
    Button as TeddyButton,
    Icon,
    IconName,
    Text,
    Notification,
    NotificationProps,
    Accordion,
    Heading,
} from "@telia/teddy";
import cn from "classnames";
import _get from "lodash/get";
import Markdown from "react-markdown";
import { connect } from "react-redux";
import { CUSTOMER_TYPE, CustomerType } from "../../../../../../constants";
import { RootState } from "../../../../../../models";
import * as properNameCase from "../../../../../../shared/properNameCase";
import { AccountMessage } from "../../../../../../models/state";
import { removeWhitespace } from "../../../../../../shared/util";
import { selectors as LOADING_SELECTORS } from "../../../../loading";
import * as CHECKOUT_AT from "../../../state/actionTypes";
import { getAvailablePhoneNumbers, getSelectedPanel } from "../../../state/selectors/phoneNumbersSelectors";
import {
    getExistingSubscriptionsInGroup,
    getAccountsWithSubscriptions,
} from "../../../state/selectors/accountSelectors";
import { getPhoneNumbersInCart } from "../../../state/selectors/combinedSelectors";
import { getSelectedPhoneNumber } from "../../../state/selectors/phoneNumberSelectors";
import {
    getLegalSubscriptionId,
    isCampaignSubscription as isCampaignSubscriptionSelector,
    isSubscriptionBroadband as isSubscriptionBroadbandSelector,
    isSubscriptionKeepExisting as isSubscriptionKeepExistingSelector,
    isSubscriptionPrepaid as isSubscriptionPrepaidSelector,
    isTeliaTrySubscription as isTeliaTrySubscriptionSelector,
} from "../../../state/selectors/subscriptionsSelectors";
import KeepNumberPanel, { KeepNumberPanelProps } from "../../molecules/KeepNumberPanel/KeepNumberPanel";
import NewNumberPanel, { NewNumberPanelProps } from "../../molecules/NewNumberPanel/NewNumberPanel";
import "./ChoosePhoneNumber.less";
import { isBroadbandCode, isZeroPlaySubscription } from "../../../../../../shared/subscriptionCodeUtils";

const phoneNumberHeadingId = "beta-checkout-phonenumber-heading";

const messageTypeToAlertKind: Record<string, NotificationProps["Root"]["variant"]> = {
    success: "success",
    alert: "warning",
    info: "information",
};

const options: { label: string; type: CustomerType }[] = [
    {
        type: CUSTOMER_TYPE.EXISTING,
        label: "Behold Telia-nummer",
    },
    {
        type: CUSTOMER_TYPE.PORT_IN,
        label: "Flytt nummer fra en annen operatør",
    },
    {
        type: CUSTOMER_TYPE.NEW,
        label: "Velg nytt nummer",
    },
];

const onboardingOptions: { label: string; type: CustomerType }[] = [
    {
        type: CUSTOMER_TYPE.EXISTING,
        label: "Behold nummer",
    },
    {
        type: CUSTOMER_TYPE.NEW,
        label: "Velg nytt nummer",
    },
];

const teliaTryOptions: { label: string; type: CustomerType }[] = [
    {
        type: CUSTOMER_TYPE.EXISTING,
        label: "Behold eksisterende nummer",
    },
    {
        type: CUSTOMER_TYPE.NEW,
        label: "Velg nytt nummer",
    },
];

const campaignOptions: { label: string; type: CustomerType }[] = [
    {
        type: CUSTOMER_TYPE.PORT_IN,
        label: "Flytt nummer fra en annen operatør",
    },
    {
        type: CUSTOMER_TYPE.NEW,
        label: "Velg nytt nummer",
    },
];

const Message = ({ messageLink, logicalMessage }: { logicalMessage: string; messageLink: string }) => {
    if (messageLink) {
        return (
            <div>
                <Markdown>{logicalMessage}</Markdown>
            </div>
        );
    }

    return <div id="phone-number-area-warning-content" dangerouslySetInnerHTML={{ __html: logicalMessage }} />;
};

interface ChoosePhoneNumberOption
    extends
        Omit<KeepNumberPanelProps, "disabled" | "labelId" | "isValidPhoneNumber">,
        Omit<NewNumberPanelProps, "disabled" | "labelId"> {
    disableKeepNumber: boolean;
    disableNewNumber: boolean;
    existingNumbersLabel: string;
    isValidInputPhoneNumber: boolean;
    type: CustomerType;
}

const ChoosePhoneNumberOption: FC<ChoosePhoneNumberOption> = ({
    type,
    choosePhoneNumber,
    selectedPhoneNumber,
    isSubscriptionBroadband,
    isSubscriptionPrepaid,
    disableKeepNumber,
    disableNewNumber,
    availablePhoneNumbers,
    fetchAvailableNumbers,
    hideNewPhoneNumberArea,
    hideNewPhoneNumberAreaMessage,
    isFetchingNumbers,
    subscriptionId,
    isValidInputPhoneNumber,
    existingPhoneNumbers,
    existingNumbersLabel,
}) => {
    switch (type) {
        case CUSTOMER_TYPE.EXISTING:
            return (
                <KeepNumberPanel
                    choosePhoneNumber={choosePhoneNumber}
                    labelId={phoneNumberHeadingId}
                    selectedPhoneNumber={selectedPhoneNumber}
                    isSubscriptionBroadband={isSubscriptionBroadband}
                    disabled={disableKeepNumber}
                    isValidPhoneNumber={isValidInputPhoneNumber}
                    existingPhoneNumbers={existingPhoneNumbers}
                    existingNumbersLabel={existingNumbersLabel}
                    data-di-mask
                />
            );
        case CUSTOMER_TYPE.PORT_IN:
            return (
                <KeepNumberPanel
                    choosePhoneNumber={choosePhoneNumber}
                    labelId={phoneNumberHeadingId}
                    selectedPhoneNumber={selectedPhoneNumber}
                    isSubscriptionBroadband={isSubscriptionBroadband}
                    disabled={disableKeepNumber}
                    isValidPhoneNumber={isValidInputPhoneNumber}
                    data-di-mask
                />
            );
        case CUSTOMER_TYPE.NEW:
            return (
                <NewNumberPanel
                    availablePhoneNumbers={availablePhoneNumbers}
                    choosePhoneNumber={choosePhoneNumber}
                    fetchAvailableNumbers={fetchAvailableNumbers}
                    hideNewPhoneNumberArea={hideNewPhoneNumberArea}
                    hideNewPhoneNumberAreaMessage={hideNewPhoneNumberAreaMessage}
                    isFetchingNumbers={isFetchingNumbers}
                    labelId={phoneNumberHeadingId}
                    selectedPhoneNumber={selectedPhoneNumber}
                    isSubscriptionBroadband={isSubscriptionBroadband}
                    isSubscriptionPrepaid={isSubscriptionPrepaid}
                    disabled={disableNewNumber}
                    data-di-mask
                    subscriptionId={subscriptionId}
                />
            );
        default: {
            return null;
        }
    }
};

interface StateProps {
    activePanel: CustomerType;
    availablePhoneNumbers: string[];
    existingNumbersLabel: string;
    existingPhoneNumbers: { firstName: string; phoneNumber: string }[];
    isCampaignSubscription: boolean;
    isCheckingNumber: boolean;
    isFetchingNumbers: boolean;
    isSubscriptionBroadband: boolean;
    isSubscriptionKeepExisting: boolean;
    isSubscriptionPrepaid: boolean;
    isTeliaTrySubscription: boolean;
}

interface ChoosePhoneNumberProps extends StateProps {
    buttonIcon?: IconName;
    buttonIconPlacement?: "left" | "right";
    buttonLabel?: string;
    choosePhoneNumber: (phoneNumber: string, isSubscriptionBroadband: boolean) => void;
    choosePhoneNumberSwitchPanel: (panel: CustomerType) => void;
    disableKeepNumber?: boolean;
    disableNewNumber?: boolean;
    fetchAvailableNumbers: () => void;
    hideNewPhoneNumberArea?: boolean;
    hideNewPhoneNumberAreaMessage?: string;
    ingress?: string;
    isAllowedToProceed?: boolean;
    isOnboarding?: boolean;
    messages: AccountMessage[];
    onButtonClick: () => void;
    selectedPhoneNumber: string;
    subscriptionId: string;
}

const ChoosePhoneNumber: FC<ChoosePhoneNumberProps> = ({
    activePanel,
    availablePhoneNumbers,
    existingNumbersLabel,
    existingPhoneNumbers,
    choosePhoneNumber,
    isOnboarding = false,
    choosePhoneNumberSwitchPanel,
    fetchAvailableNumbers,
    hideNewPhoneNumberArea = false,
    hideNewPhoneNumberAreaMessage = "",
    isFetchingNumbers,
    isCheckingNumber,
    isCampaignSubscription,
    messages,
    selectedPhoneNumber,
    isSubscriptionBroadband,
    isSubscriptionKeepExisting,
    isSubscriptionPrepaid,
    isTeliaTrySubscription,
    ingress,
    disableKeepNumber = false,
    disableNewNumber = false,
    subscriptionId,
    isAllowedToProceed,
    onButtonClick,
    buttonLabel,
    buttonIcon,
    buttonIconPlacement,
}) => {
    const [internalErrorMessage, setInternalErrorMessage] = useState("");
    const logicalMessage = _get(messages, "[0].text", "");
    const messageLink = _get(messages, "[0].link", "");
    const isMessageSuccess = _get(messages, "[0].type") === "success";
    const [expandedMessages, setExpandedMessages] = useState(new Set<number>());
    const messageIsForNewCustomer = _get(messages, "[0].isNewCustomerMessage");
    const onlyDigits = (selectedPhoneNumber || "").replace(/\s/g, "");
    const isValidInputPhoneNumber = onlyDigits.length === 8 && isMessageSuccess;

    const filteredOptions = useMemo(() => {
        if (isOnboarding) {
            return onboardingOptions;
        }
        if (isCampaignSubscription) {
            return campaignOptions;
        }
        if (isTeliaTrySubscription) {
            return teliaTryOptions;
        }
        if (isSubscriptionKeepExisting) {
            return options.slice(0, 1);
        }

        return options;
    }, [isCampaignSubscription, isSubscriptionKeepExisting, isTeliaTrySubscription]);

    const handleButtonClick = () => {
        if (isAllowedToProceed) {
            onButtonClick();
            setInternalErrorMessage("");
        } else {
            setInternalErrorMessage("Vennligst fullfør et av alternativene nedenfor");
        }
    };

    const handleChoosePhoneNumber = (sId: string, isBroadband: boolean) => {
        setInternalErrorMessage("");
        return choosePhoneNumber(sId, isBroadband);
    };

    const getIsSelected = (type: CustomerType): boolean => {
        if (isTeliaTrySubscription) {
            // Only existing and new is shown for try subscription => portin goes under existing panel
            if (type === CUSTOMER_TYPE.EXISTING && activePanel === CUSTOMER_TYPE.PORT_IN) {
                return true;
            }
        }
        return type === activePanel;
    };

    return (
        <Container className="choose-phone-number" size="small" noMargin noPadding whiteBg>
            {internalErrorMessage && (
                <div className="choose-phone-number__error-message">
                    <Icon name="error-filled" />
                    <Text mb="100">{internalErrorMessage}</Text>
                </div>
            )}
            {ingress && (
                <Text mb="100" className="beta-checkout__ingress">
                    {ingress}
                </Text>
            )}
            {filteredOptions.map(({ type, label }, index) => {
                const isSelected = getIsSelected(type);
                return (
                    <div key={`choose-phonenumber-option-${type}`}>
                        {index > 0 && <hr className="choose-phone-number__divider" />}
                        <div
                            className={cn("choose-phone-number__option", {
                                "choose-phone-number__option--selected": isSelected,
                            })}
                        >
                            <RadioButtonWithLabel
                                value={type}
                                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                                    const newPanelSide = event.target.value as CustomerType;
                                    if (newPanelSide && newPanelSide !== activePanel) {
                                        setInternalErrorMessage("");
                                        choosePhoneNumberSwitchPanel(newPanelSide);
                                    }
                                }}
                                name="choose-phonenumber"
                                hasRichContent
                                checked={isSelected}
                            >
                                <Heading
                                    mb="100"
                                    as="h3"
                                    variant="title-100"
                                    className="choose-phone-number__option-heading"
                                >
                                    {label}
                                </Heading>
                            </RadioButtonWithLabel>
                            {isSelected && (
                                <div className="choose-phone-number__expanded">
                                    <ChoosePhoneNumberOption
                                        type={type}
                                        availablePhoneNumbers={availablePhoneNumbers}
                                        existingPhoneNumbers={existingPhoneNumbers}
                                        existingNumbersLabel={existingNumbersLabel}
                                        choosePhoneNumber={handleChoosePhoneNumber}
                                        fetchAvailableNumbers={fetchAvailableNumbers}
                                        hideNewPhoneNumberArea={hideNewPhoneNumberArea}
                                        hideNewPhoneNumberAreaMessage={hideNewPhoneNumberAreaMessage}
                                        isFetchingNumbers={isFetchingNumbers}
                                        selectedPhoneNumber={selectedPhoneNumber}
                                        isSubscriptionBroadband={isSubscriptionBroadband}
                                        isSubscriptionPrepaid={isSubscriptionPrepaid}
                                        disableNewNumber={disableNewNumber}
                                        data-di-mask
                                        subscriptionId={subscriptionId}
                                        disableKeepNumber={disableKeepNumber}
                                        isValidInputPhoneNumber={isValidInputPhoneNumber}
                                    />
                                    {messageIsForNewCustomer && (
                                        <div className="choose-phone-number__new-customer-message-container">
                                            <span className="choose-phone-number__new-customer-message-icon">
                                                <Icon name="smiley-happy" />
                                            </span>
                                            <Message messageLink={messageLink} logicalMessage={logicalMessage} />
                                        </div>
                                    )}
                                    {!messageIsForNewCustomer &&
                                        messages
                                            .filter((msg) => msg.text)
                                            // Ignore (less important) info messages if there are alert messages
                                            .filter((msg) => {
                                                const hasAlertMessages = messages.some((m) => m.type === "alert");
                                                if (hasAlertMessages) {
                                                    return msg.type === "alert";
                                                }
                                                return true;
                                            })
                                            .sort((a, b) => {
                                                const aIsAccordion = a.display === "accordion" ? 1 : 0;
                                                const bIsAccordion = b.display === "accordion" ? 1 : 0;
                                                return aIsAccordion - bIsAccordion;
                                            })
                                            .slice(0, 3)
                                            .map((msg, index) => {
                                                if (msg.display === "accordion") {
                                                    return (
                                                        <Accordion key={index} type="single" collapsible>
                                                            <Accordion.Item
                                                                value={`msg-${index}`}
                                                                className="choose-phone-number__accordion-item"
                                                            >
                                                                <Heading
                                                                    mb="100"
                                                                    variant="subsection-100"
                                                                    as="h5"
                                                                    className="choose-phone-number__accordion-heading"
                                                                >
                                                                    <Accordion.Header asChild>
                                                                        <Accordion.Trigger className="choose-phone-number__accordion-trigger">
                                                                            {msg.heading}
                                                                        </Accordion.Trigger>
                                                                    </Accordion.Header>
                                                                </Heading>
                                                                <Accordion.Content>
                                                                    <div
                                                                        className="paragraph paragraph--fineprint"
                                                                        dangerouslySetInnerHTML={{ __html: msg.text }}
                                                                    />
                                                                </Accordion.Content>
                                                            </Accordion.Item>
                                                        </Accordion>
                                                    );
                                                }

                                                const msgLink = msg.link || "";
                                                const alertKind =
                                                    messageTypeToAlertKind[msg.type as unknown as string] ?? "warning";
                                                const msgExpandedText = msg.expandedText;
                                                const isExpanded = expandedMessages.has(index);
                                                return (
                                                    <Notification key={index} variant={alertKind}>
                                                        {!msg.heading ? (
                                                            <Notification.Icon />
                                                        ) : (
                                                            <Notification.Heading as="h3">
                                                                <Notification.Icon>{msg.heading}</Notification.Icon>
                                                            </Notification.Heading>
                                                        )}
                                                        <Notification.Text className="choose-phone-number__notification-text">
                                                            {msgExpandedText ? (
                                                                <div
                                                                    id={
                                                                        index === 0
                                                                            ? "phone-number-area-warning-content"
                                                                            : undefined
                                                                    }
                                                                >
                                                                    <p>{isExpanded ? msgExpandedText : msg.text}</p>
                                                                    {!isExpanded && (
                                                                        <TeddyButton
                                                                            onClick={(): void => {
                                                                                setExpandedMessages((prev) =>
                                                                                    new Set(prev).add(index),
                                                                                );
                                                                            }}
                                                                            variant="text"
                                                                        >
                                                                            Vis mer
                                                                        </TeddyButton>
                                                                    )}
                                                                </div>
                                                            ) : msgLink ? (
                                                                <Message
                                                                    messageLink={msgLink}
                                                                    logicalMessage={msg.text}
                                                                />
                                                            ) : (
                                                                <div
                                                                    id={
                                                                        index === 0
                                                                            ? "phone-number-area-warning-content"
                                                                            : undefined
                                                                    }
                                                                    dangerouslySetInnerHTML={{ __html: msg.text }}
                                                                />
                                                            )}
                                                        </Notification.Text>
                                                    </Notification>
                                                );
                                            })}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
            {isCampaignSubscription && (
                <Text mb="100">
                    <i>Kampanjeprisen gjelder nye abonnement med 12 md. binding.</i>
                </Text>
            )}
            {!isOnboarding && (
                <div className="add-member__step-button-container">
                    <TeddyButton
                        className="add-member__step-button"
                        disabled={
                            isCheckingNumber ||
                            isFetchingNumbers ||
                            !selectedPhoneNumber ||
                            removeWhitespace(selectedPhoneNumber)?.length < 8 ||
                            !isAllowedToProceed
                        }
                        loading={isCheckingNumber || isFetchingNumbers}
                        onClick={handleButtonClick}
                    >
                        {buttonIconPlacement === "left" && buttonIcon && (
                            <Icon className="add-member__step-button-icon" name={buttonIcon} />
                        )}
                        {buttonLabel}
                        {buttonIconPlacement === "right" && buttonIcon && (
                            <Icon className="add-member__step-button-icon" name={buttonIcon} />
                        )}
                    </TeddyButton>
                </div>
            )}
        </Container>
    );
};

const mapStateToProps: (state: RootState, ownProps: { subscriptionId: string }) => StateProps = (
    state: RootState,
    ownProps: { subscriptionId: string },
) => {
    const subscriptionId = ownProps.subscriptionId || getLegalSubscriptionId(state);
    const currentNumber = getSelectedPhoneNumber(state, subscriptionId);
    const numbersInCart = new Set(getPhoneNumbersInCart(state).filter((n) => n !== currentNumber));

    return {
        activePanel: getSelectedPanel(state),
        availablePhoneNumbers: getAvailablePhoneNumbers(state),
        existingNumbersLabel:
            getAccountsWithSubscriptions(state).length > 1 ? "Dine numre på denne fakturaen" : "Dine numre",
        existingPhoneNumbers: getExistingSubscriptionsInGroup(state)
            .filter(
                (sub) =>
                    Boolean(sub.phoneNumber) &&
                    !numbersInCart.has(sub.phoneNumber) &&
                    !isZeroPlaySubscription(sub.offeringCode) &&
                    !isBroadbandCode(sub.offeringCode),
            )
            .map((sub) => ({ phoneNumber: sub.phoneNumber, firstName: properNameCase.convert(sub.firstName ?? "") })),
        isFetchingNumbers: LOADING_SELECTORS.isTypeLoading(state, CHECKOUT_AT.CHECKOUT_FETCH_AVAILABLE_PHONE_NUMBERS),
        isCheckingNumber: LOADING_SELECTORS.isTypeLoading(state, CHECKOUT_AT.CHECKOUT_CHECK_NEW_MEMBER_PHONE_NUMBER),
        isCampaignSubscription: isCampaignSubscriptionSelector(state, subscriptionId),
        isSubscriptionBroadband: isSubscriptionBroadbandSelector(state, subscriptionId),
        isSubscriptionKeepExisting: isSubscriptionKeepExistingSelector(state, subscriptionId),
        isSubscriptionPrepaid: isSubscriptionPrepaidSelector(state, subscriptionId),
        isTeliaTrySubscription: isTeliaTrySubscriptionSelector(state, subscriptionId),
        subscriptionId,
    };
};

export default connect(mapStateToProps)(ChoosePhoneNumber);
